import moment, { Moment } from 'moment-timezone'
import { createContext, useContext, useState, ReactNode, useEffect, useRef, useMemo } from 'react'

/**
 * Interval duration for time updates (30 seconds)
 * Keeps all time-based calculations in sync across the app
 */
const TIME_UPDATE_INTERVAL_MS = 30000

interface CurrentTimeContextValue {
  currentTime: Moment
}

const CurrentTimeContext = createContext<CurrentTimeContextValue | null>(null)

/**
 * Provider that maintains a shared current time reference
 * Updates at the top of each minute (xx:xx:00) and every 30 seconds thereafter
 * This ensures status changes happen precisely when meetings start/end
 *
 * @param mockTime - Optional fixed time to use (useful for Storybook/testing)
 */
export const CurrentTimeProvider = ({ children, mockTime }: { children: ReactNode; mockTime?: Moment }) => {
  // Clone mockTime to ensure it's immutable and doesn't change
  const getInitialTime = () => {
    if (mockTime) {
      return mockTime.clone()
    }
    return moment()
  }
  const [currentTime, setCurrentTime] = useState(getInitialTime)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // If mockTime is provided, use it and don't set up timers
    if (mockTime) {
      setCurrentTime(mockTime.clone())
      return undefined
    }

    const scheduleNextUpdate = () => {
      // Clear existing timers
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)

      const now = moment()
      const seconds = now.seconds()
      const milliseconds = now.milliseconds()

      // Calculate milliseconds until the next top of the minute (xx:xx:00.000)
      const msUntilNextMinute = (60 - seconds) * 1000 - milliseconds

      // Schedule update at the top of the next minute
      timeoutRef.current = setTimeout(() => {
        setCurrentTime(moment())

        // After syncing to the top of the minute, set up regular 30-second intervals
        // starting from this synchronized point
        intervalRef.current = setInterval(() => {
          setCurrentTime(moment())
        }, TIME_UPDATE_INTERVAL_MS)
      }, msUntilNextMinute)
    }

    // Initial sync
    scheduleNextUpdate()

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [mockTime])

  const value = useMemo(() => ({ currentTime }), [currentTime])

  return (
    <CurrentTimeContext.Provider value={value}>
      {children}
    </CurrentTimeContext.Provider>
  )
}

/**
 * Hook to access the shared current time
 * Falls back to moment() if not within CurrentTimeProvider
 */
export const useCurrentTime = (): Moment => {
  const context = useContext(CurrentTimeContext)

  // Fallback for components not wrapped in provider (backwards compatibility)
  if (!context) {
    return moment()
  }

  return context.currentTime
}
